<template>
  <div class="comments-view d-flex flex-column h-100">
    <!-- Header -->
    <div class="comments-header d-flex align-center justify-space-between pa-3 border-bottom">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-chat-outline</v-icon>
        <span class="text-subtitle-1 font-weight-medium">{{ $t('comments') }}</span>
      </div>
      <div class="d-flex align-center">
        <v-switch
          v-model="showHistory"
          :label="$t('history')"
          dense
          hide-details
          class="mt-0 pt-0"
        ></v-switch>
      </div>
    </div>

    <!-- Comments List -->
    <div ref="commentsContainer" class="comments-list flex-grow-1 overflow-y-auto pa-2">
      <div v-if="loading" class="d-flex justify-center align-center h-100">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="filteredComments.length === 0" class="d-flex flex-column align-center justify-center h-100 text-center">
        <v-icon large color="grey lighten-1" class="mb-2">mdi-comment-multiple-outline</v-icon>
        <span class="grey--text">{{ $t('comments_container') }}</span>
      </div>

      <div v-else>
        <!-- Load More Comments Button -->
        <div v-if="count < total" class="load-more-container d-flex justify-center mb-4">
          <v-btn
            outlined
            color="primary"
            :loading="false"
            @click="loadMoreComments"
            class="load-more-btn"
          >
            <v-icon left small>mdi-chevron-up</v-icon>
            {{ $t('load_more_comments') }}
          </v-btn>
        </div>

        <div
          v-for="comment in filteredComments"
          :key="comment.comment_id"
          class="comment-item mb-3"
        >
          <!-- Chat Comment Layout -->
          <div v-if="comment.comment_type === 1" class="chat-comment pa-3 rounded-lg rounded-tl-0">
            <div class="d-flex flex-column align-start">
              <span
                  class="font-weight-medium"
                >{{ getUserName(comment.users_id) }} • {{ getUserGroupName(comment.users_id) }}</span>

                <span class="grey--text text--darken-1">
                  {{ formatDateHistory(comment.created_date) }}
                </span>
            </div>
            <div
              @click.stop="toggleExpandComment"
              class="comment-message"
              v-html="highlightMentions(comment.message)"
            ></div>
          </div>

          <!-- History Comment Layout -->
          <div v-else-if="comment.comment_type === 2" class="history-comment d-flex">
            <div class="history-content flex-grow-1">
              <div class="d-flex flex-column align-start">
                <span
                  class="font-weight-medium"
                >{{ getUserName(comment.users_id) }} • {{ getUserGroupName(comment.users_id) }}</span>

                <span class="grey--text text--darken-1">
                  {{ formatDateHistory(comment.created_date) }}
                </span>
              </div>
              <div
                @click.stop="toggleExpandComment"
                class="comment-message grey--text text--darken-1"
                v-html="comment.message"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      v-if="currentMode != 'consult'"
      class="comments-footer pa-3 border-top"
    >
      <div class="d-flex align-center">
        <Mentionable
          @open="onMentionOpen"
          @close="onMentionClose"
          @apply="onMentionSelect"
          :keys="['#']"
          :items="mentionUsers"
          :item-key="'value'"
          offset="6"
          insert-space
          class="flex-grow-1 mr-2"
        >
          <v-textarea
            v-model="newComment"
            @keydown.ctrl.enter="sendComment"
            :placeholder="$t('type_your_comment')"
            :label="$t('send_comment')"
            :hint="$t('job_comment_hint')"
            :disabled="!jobId"
            rows="3"
            outlined
            dense
            persistent-hint
          ></v-textarea>

          <template #no-result>
            <div class="dim">
              {{ 'No result' }}
            </div>
          </template>

          <template #item="{ item }">
            <span>
                #{{ item.label }}
            </span>
          </template>
        </Mentionable>

        <v-btn
          icon
          color="primary"
          :disabled="!newComment.trim() || sending || !jobId"
          @click="sendComment"
          :loading="sending"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import CommentAPI from '@/services/api/job/comment'
import { Mentionable } from 'vue-mention'

import 'floating-vue/dist/style.css'

export default {
  name: 'CommentsView',

  components: {
    Mentionable,
  },

  props: {
    jobId: {
      type: [Number, String],
      default: null
    },
    groups: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
    currentMode: {
      type: String,
      default: "consult"
    },
  },

  data() {
    return {
      showHistory: false,
      comments: [],
      newComment: '',
      mentionedUserIds: [],
      loading: false,
      sending: false,
      limit: 20,
      count: 0,
      total: 0,
      dropdownClickHandler: null
    }
  },

  mounted() {
    // Add global click handler to stop propagation from mention dropdown
    this.dropdownClickHandler = (event) => {
      const target = event.target
      // Check if click is inside mention dropdown
      if (target.closest('.tooltip.popover.vue-popover-theme')) {
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
    }

    // Attach to capture phase to ensure it runs first
    document.addEventListener('click', this.dropdownClickHandler, true)
  },

  beforeDestroy() {
    // Clean up event listener
    if (this.dropdownClickHandler) {
      document.removeEventListener('click', this.dropdownClickHandler, true)
    }
  },

  computed: {
    mentionUsers() {
      // Get unique user IDs who have commented
      const commentedUserIds = new Set(
        this.comments.map(comment => comment.users_id)
      )

      // Map users to mention format - vue-mention needs 'value' and optionally 'label'
      const allMentionUsers = this.users.map(u => ({
        value: u.name,  // This is what gets inserted
        label: u.name,  // This is what gets displayed
        users_id: u.users_id,
        hasCommented: commentedUserIds.has(u.users_id)
      }))

      // Sort: users who have commented first, then others
      const sorted = allMentionUsers.sort((a, b) => {
        if (a.hasCommented && !b.hasCommented) return -1
        if (!a.hasCommented && b.hasCommented) return 1
        // Keep original order for users in the same category
        return 0
      })

      return sorted
    },

    filteredComments() {
      if (this.showHistory) {
        return this.comments
      } else {
        return this.comments.filter(comment => comment.comment_type === 1)
      }
    },

    hasUserComments() {
      // Check if there are any user comments (comment_type === 1)
      return this.comments.some(comment => comment.comment_type === 1)
    }
  },

  watch: {
    jobId: {
      immediate: true,
      async handler(newJobId) {
        if (newJobId) {
          // load comments with true to scroll to botton
          this.loadComments(true)
        }
      }
    },
    newComment(newVal) {
      this.syncMentions(newVal)
    },
  },

  methods: {
    async loadComments($scroll) {
      if (!this.jobId) return

      this.loading = true
      try {
        let options = {...this.$route.query}
        options.order = "created_date"
        options.direction = "DESC"
        options.limit = this.limit
        const response = await CommentAPI.getJobComments(this.jobId, options)

        // Handle paginated response
        if (response.data && response.data.items) {
          this.comments = response.data.items
          this.count = this.comments.length
          this.total = response.data.total
        } else {
          this.comments = []
        }

        // Sort comments by date (oldest first, most recent at bottom)
        this.comments.sort((a, b) => new Date(a.created_date) - new Date(b.created_date))

        // Load user data for comments
        //await this.loadUserData()

        // Scroll to bottom after loading comments
        if ($scroll) {
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }

        // Emit event to notify parent about user comments presence
        this.$emit('comments-loaded', this.hasUserComments)
      } catch (error) {
        console.error('Error loading comments:', error)
        this.comments = []
      } finally {
        this.loading = false
      }
    },

    async loadMoreComments() {
      // Increment limit by 20 to load more comments
      this.limit += 20
      let options = {...this.$route.query}
      options.limit = this.limit

      // Reload comments with the new limit and false to does not scroll
      await this.loadComments(false)
    },

    async sendComment() {
      if (!this.newComment.trim() || this.sending) return

      this.sending = true
      try {
        const commentData = {
          job_id: parseInt(this.jobId),
          message: this.newComment.trim(),
          comment_type: 1,
          mentioned_user_ids: this.mentionedUserIds
        }

        await CommentAPI.createComment(commentData)

        // Clear the input
        this.newComment = ''

        // Reload comments to show the new one with true to scroll to botton
        await this.loadComments(true)

        // Ensure scroll to bottom after sending new comment
        this.$nextTick(() => {
          this.$nextTick(() => {
            setTimeout(() => {
              this.scrollToBottom()
            }, 100)
          })
        })

        this.$emit('comment-added')
      } catch (error) {
        console.error('Error sending comment:', error)
        this.$emit('error', 'Failed to send comment')
      } finally {
        this.sending = false
      }
    },

    getCurrentUserId() {
      // Get current user ID from store
      const currentUser = this.$store.getters['core/user/getCurrentUser']
      return currentUser?.user_id || 1
    },

    getUserName(userId) {
      const userObj = this.users.find(obj => obj.users_id === userId);
      return userObj?.name || ''
    },

    getUserGroupName(userId) {
      const userObj = this.users.find(obj => obj.users_id === userId);
      const groupIds = userObj.group_ids
      for (const groupId of groupIds) {
        const group = this.groups.find(obj => obj.user_group_id === groupId);
        return group?.group_title || ''
      }
    },

    formatDateComment(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${day}/${month}/${year} ${hours}:${minutes}`
    },

    formatDateHistory(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${day}/${month}/${year}\n${hours}:${minutes}`
    },

    scrollToBottom() {
      // Scroll to bottom of comments container with better timing
      if (this.$refs.commentsContainer) {
        const container = this.$refs.commentsContainer

        // Use multiple nextTicks to ensure DOM is fully updated
        this.$nextTick(() => {
          this.$nextTick(() => {
            // Small delay to ensure all elements are rendered
            setTimeout(() => {
              container.scrollTop = container.scrollHeight
            }, 50)
          })
        })
      }
    },

    toggleExpandComment(e) {
      if (e.currentTarget.classList.contains('expanded')) {
        e.currentTarget.classList.remove('expanded')

        return
      }

      e.currentTarget.classList.add('expanded')
    },

    syncMentions(text) {
      const names =
        text.match(/#([^\s#]+)/g)?.map(v => v.slice(1)) || []

      const visibleIds = this.mentionUsers
        .filter(u => names.includes(u.label))
        .map(u => u.users_id)

      this.mentionedUserIds = this.mentionedUserIds
        .filter(id =>
          visibleIds.includes(id)
        )
    },

    onMentionSelect(item) {
      const userId = item.users_id

      if (!this.mentionedUserIds.includes(userId)) {
        this.mentionedUserIds.push(userId)
      }

      // Manually trigger close after selection
      this.$nextTick(() => {
        this.onMentionClose()
      })
    },

    onMentionOpen() {
      this.$emit('mention-opened')

      // Force position update after dropdown content is rendered
      this.$nextTick(() => {
        this.$nextTick(() => {
          // Find the popper element and trigger a position update
          const popperElement = document.querySelector('.tooltip.popover.vue-popover-theme')
          if (popperElement && popperElement._tippy) {
            popperElement._tippy.popperInstance.update()
          }
        })
      })
    },

    onMentionClose() {
      this.$emit('mention-closed')
    },

    highlightMentions(text) {
      if (!text) return ''

      // Escape HTML to prevent XSS attacks
      const escapeHtml = (str) => {
        return str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
      }

      // Escape the text first
      let escapedText = escapeHtml(text)

      // Replace #mentions with highlighted spans
      // Match #username patterns (username can contain letters, numbers, spaces, dots, underscores, hyphens)
      const mentionRegex = /#([a-zA-Z0-9\s._-]+?)(?=\s|$|[,.!?;:])/g

      escapedText = escapedText.replace(mentionRegex, (match, username) => {
        return `<span class="mention-highlight">#${username}</span>`
      })

      return escapedText
    },
  }
}
</script>

<style>
/* Force mention dropdown to render above everything */
.v-popper__popper,
.mention-dropdown,
.tooltip.popover.vue-popover-theme {
  z-index: 10000 !important;
  position: fixed !important;
}

/* Only show when not hidden - respect library's aria-hidden state */
.tooltip.popover.vue-popover-theme[aria-hidden="false"] {
  visibility: visible !important;
  display: block !important;
}

.tooltip.popover.vue-popover-theme[aria-hidden="true"] {
  visibility: hidden !important;
  display: none !important;
}

/* Ensure dropdown is not clipped by parent containers */
.v-popper__popper[data-popper-placement] {
  position: fixed !important;
  z-index: 10000 !important;
}

/* Style the popover wrapper and inner */
.tooltip.popover.vue-popover-theme .wrapper {
  background: #fff !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid #e0e0e0 !important;
}

.tooltip.popover.vue-popover-theme .tooltip-inner,
.tooltip.popover.vue-popover-theme .popover-inner {
  background: #fff !important;
  max-height: 200px !important;
  min-height: 40px !important;
  overflow-y: auto !important;
  padding: 4px 0 !important;
  width: auto !important;
  min-width: 200px !important;
  border-radius: 8px !important;
}

/* Style the mention items */
.tooltip.popover.vue-popover-theme .mention-item {
  padding: 12px 16px !important;
  cursor: pointer !important;
  transition: background-color 0.2s ease !important;
  border-bottom: 1px solid #f0f0f0 !important;
  font-size: 14px !important;
  color: #333 !important;
  display: block !important;
  pointer-events: auto !important;
}

/* Prevent click events from propagating to parent elements */
.tooltip.popover.vue-popover-theme {
  pointer-events: auto !important;
}

.tooltip.popover.vue-popover-theme .wrapper,
.tooltip.popover.vue-popover-theme .tooltip-inner,
.tooltip.popover.vue-popover-theme .popover-inner {
  pointer-events: auto !important;
}

.tooltip.popover.vue-popover-theme .mention-item:last-child {
  border-bottom: none !important;
}

.tooltip.popover.vue-popover-theme .mention-item:hover {
  background-color: #f5f5f5 !important;
}

.tooltip.popover.vue-popover-theme .mention-item.mention-selected {
  background-color: var(--v-warning-lighten5) !important;
  border-left: 3px solid var(--v-warning-base) !important;
  padding-left: 13px !important;
}

/* Hide the arrow */
.tooltip.popover.vue-popover-theme .tooltip-arrow,
.tooltip.popover.vue-popover-theme .popover-arrow {
  display: none !important;
}
</style>

<style scoped>
/* Mention style for displayed comments */
::v-deep .mention-highlight {
  color: var(--v-warning-base);
  font-weight: 500;
  background-color: var(--v-warning-lighten5);
  padding: 2px 4px;
  border-radius: 3px;
}

/* Mention dropdown styling */
.mention-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.mention-item:last-child {
  border-bottom: none;
}

.mention-item:hover {
  background-color: #f5f5f5;
}

.mention-item[data-selected="true"],
.mention-item.selected {
  background-color: var(--v-warning-lighten5) !important;
  border-left: 3px solid var(--v-warning-base);
  padding-left: 13px;
}

/* Legacy tribute.js classes (if used) */
.tribute-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10000 !important;
}

.tribute-container ul {
  list-style: none;
  padding: 4px 0;
  margin: 0;
}

.tribute-container li {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.tribute-container li:last-child {
  border-bottom: none;
}

.tribute-container li:hover,
.tribute-container li.highlight {
  background-color: var(--v-warning-lighten5) !important;
  border-left: 3px solid var(--v-warning-base);
  padding-left: 13px;
}

.comments-view {
  height: 100%;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0 !important;
}

.border-top {
  border-top: 1px solid #e0e0e0 !important;
}

.comments-list {
  max-height: calc(100vh - 200px);
}

.comment-item {
  position: relative;
}

.chat-comment {
  background-color: #f5f5f5;
  font-size: 14px;
}

.chat-comment .text-caption {
  white-space: pre-line;
}

.history-comment {
  padding: 8px 0;
}

.history-date {
  padding-top: 2px;
  text-align: right;
  white-space: pre-line;
}

.vertical-line {
  width: 2px;
  height: 100%;
  background-color: #e0e0e0;
  min-height: 40px;
}

.history-divider {
  display: flex;
  align-items: stretch;
}

.history-content {
  padding-left: 8px;
  font-size: 14px;
  flex: 1 1 0; /* allow shrinking inside flex container */
  min-width: 0; /* THIS is the key line */
}

.comment-message {
  display: block;
  max-width: 100%; /* makes ellipsis work within parent width */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: all 0.2s ease;
  max-height: 1.5em;
}

.comment-message.expanded {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
  max-height: 100%;
}

div.comment-message >>> b {
  color: black !important;
}

.load-more-container {
  padding: 16px 0;
}

.load-more-btn {
  border-radius: 20px;
  text-transform: none;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .history-comment {
    flex-direction: column;
  }

  .history-date {
    text-align: left;
    margin-bottom: 8px;
  }

  .history-divider {
    display: none;
  }

  .history-content {
    padding-left: 0;
  }
}
</style>
